/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps } from '@msdyn365-commerce/core';
import { ProductList } from '@msdyn365-commerce/retail-proxy';
import {
    StringExtensions,
    updateOrderTemplateHeader,
    UpdateOrderTemplateHeaderInput
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
import { createCountingListAsync } from '../../../actions/create-counting-list.action';

export interface ICountingListNameDialogResources {
    countingListHeaderLabel: string;
    countingListTitleLabel: string;
    countingListNameAriaLabel: string;
    countingListDescription: string;
    defaultCountingListName: string;
    countingListButtonLabel: string;
    countingListCancelButtonLabel: string;
    countingListModalAriaLabel?: string;
}

export interface ICountingListNameDialogProps extends IComponentProps {
    isUpdateDialog: boolean | undefined;
    existingCountingList?: ProductList;
    className: string;
    countingListName?: string;
    dialogStrings: ICountingListNameDialogResources;
    modalOpen: boolean | undefined;
    parentElementRef?: React.RefObject<HTMLButtonElement>;
    setModalOpen(newValue: boolean): void;
    onCancel(): void;
    onSuccess(countingList: ProductList): Promise<void>;
}

export const CountingListNameDialog = (dialogProps: ICountingListNameDialogProps) => {
    const {
        isUpdateDialog,
        existingCountingList,
        dialogStrings,
        className,
        modalOpen,
        setModalOpen,
        onCancel,
        onSuccess,
        context,
        parentElementRef
    } = dialogProps;

    if (!dialogStrings) {
        return null;
    }

    const [countingListName, setCountingListName] = useState(dialogProps.countingListName);

    const cancel: () => void = () => {
        setModalOpen(false);
        onCancel();
        setCountingListName('');
        if (parentElementRef) {
            parentElementRef.current?.focus();
        }
    };

    const submit: () => Promise<void> = async () => {
        dialogProps.countingListName = countingListName || dialogStrings.defaultCountingListName;
        if (StringExtensions.isNullOrWhitespace(dialogProps.countingListName)) {
            return;
        }
        if (isUpdateDialog) {
            if (existingCountingList) {
                existingCountingList.Name = dialogProps.countingListName;
                const input: UpdateOrderTemplateHeaderInput = new UpdateOrderTemplateHeaderInput(existingCountingList);
                const countingList: ProductList = await updateOrderTemplateHeader(input, context.actionContext);
                setModalOpen(false);
                await onSuccess(countingList);
            } else {
                context.telemetry.error('You need to pass ProductList instance for update.');
                throw new Error('Unexpected error: existing order template was not set.');
            }
        } else {
            const countingList: ProductList = await createCountingListAsync(
                { callerContext: context.actionContext },
                undefined, undefined, [],
                {
                    Id: '',
                    Name: dialogProps.countingListName,
                    ExtensionProperties: [],
                    ProductListLines: []
                }
            );
            setModalOpen(false);
            await onSuccess(countingList);
        }
    };

    const updateCountingListName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCountingListName(event.target.value);
    };

    return (
        <Modal
            isOpen={modalOpen}
            className={`${className} msc-modal-input-required`}
            toggle={cancel}
            aria-label={dialogStrings.countingListModalAriaLabel}
        >
            <ModalHeader className={`${className}__dialog__header`} toggle={cancel}>
                <span aria-live='assertive'>{dialogStrings.countingListHeaderLabel}</span>
            </ModalHeader>
            <ModalBody className={`${className}__dialog__body`}>
                <div className={`${className}__dialog__content`}>
                    <label
                        className={`${className}__dialog__order-template-name-label`}
                        htmlFor={`${className}__dialog__order-template-name`}
                    >
                        {dialogStrings.countingListTitleLabel}
                    </label>
                    <input
                        className={`${className}__dialog__order-template-name`}
                        type='text'
                        aria-label={dialogStrings.countingListNameAriaLabel}
                        placeholder={dialogStrings.defaultCountingListName}
                        onChange={updateCountingListName}
                        value={countingListName}
                        maxLength={250}
                    />
                    <div className={`${className}__dialog__description`}>{dialogStrings.countingListDescription}</div>
                </div>
            </ModalBody>
            <ModalFooter className={`${className}__dialog__footer`}>
                <Button
                    className={`${className}__dialog__create-order-template-button`}
                    title={dialogStrings.countingListButtonLabel}
                    aria-label={dialogStrings.countingListButtonLabel}
                    onClick={submit}
                >
                    {dialogStrings.countingListButtonLabel}
                </Button>
                <Button
                    className={`${className}__dialog__cancel-button`}
                    title={dialogStrings.countingListCancelButtonLabel}
                    aria-label={dialogStrings.countingListCancelButtonLabel}
                    onClick={cancel}
                >
                    {dialogStrings.countingListCancelButtonLabel}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
